import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import type { SalesOrderStatus } from '../../../types';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import { PURCHASE_ORDER_LINE_ITEMS_EDITABLE_STATUSES } from './purchaseOrder';

export type SalesOrderLineItemRowKind = 'internal-parent' | 'source-child' | 'external';
export type SalesOrderLineItemPriceMode = 'sellPrice' | 'deliveredPrice' | 'both';

export type SalesOrderLineItemFacts = {
  salesOrderStatus: SalesOrderStatus;
  rowKind: SalesOrderLineItemRowKind;
  /**
   * null = no linked PO; undefined = linked PO whose status has not hydrated.
   * The undefined case stays locked, matching the current member grid.
   */
  linkedPurchaseOrderStatus: $Enums.PurchaseOrderStatus | null | undefined;
  priceMode: SalesOrderLineItemPriceMode;
  enteredByWeight: boolean;
  variableWeight: boolean;
  hasLot: boolean;
  hasAnySourceLot: boolean;
  isDraft: boolean;
  /** At least one live AR invoice bills this line, and the bill page still edits every one of them. */
  isOnEditableInvoice: boolean;
  /** At least one live AP payable bills this line, and the bill page still edits every one of them. */
  isOnEditablePayable: boolean;
};

export const SALES_ORDER_LINE_ITEM_FIELDS = [
  'product',
  'external',
  'buyPrice',
  'unitsOrdered',
  'weightOrdered',
  'sellPrice',
  'deliveredPrice',
  'enteredByWeight',
  'unitsPicked',
  'weightPicked',
  'source',
  'palletTagsOnLineItem',
] as const;

export type SalesOrderLineItemField = (typeof SALES_ORDER_LINE_ITEM_FIELDS)[number];

export const SALES_ORDER_LINE_ITEM_OPS = ['clear', 'scan', 'pick', 'autoPick'] as const;
export type SalesOrderLineItemOp = (typeof SALES_ORDER_LINE_ITEM_OPS)[number];

const EDITABLE_ORDER_STATUSES: readonly SalesOrderStatus[] = ['DRAFT', 'PENDING', 'SUBMITTED', 'APPROVED', 'PICKING'];

/** The price an editable bill still prices — open past the status and purchase-order locks. */
const isPricedByEditableBill = (facts: SalesOrderLineItemFacts, field: SalesOrderLineItemField): boolean =>
  ((field === 'sellPrice' || field === 'deliveredPrice') && facts.isOnEditableInvoice) ||
  (field === 'buyPrice' && facts.isOnEditablePayable);

const entityLock = (facts: SalesOrderLineItemFacts): Decision => {
  if (!EDITABLE_ORDER_STATUSES.includes(facts.salesOrderStatus)) {
    return deny(
      'SALES_ORDER_PRODUCTS_LOCKED',
      `Products are locked once the sales order is ${getStatusLabel(facts.salesOrderStatus)}.`
    );
  }

  if (
    facts.linkedPurchaseOrderStatus !== null &&
    (facts.linkedPurchaseOrderStatus === undefined ||
      !PURCHASE_ORDER_LINE_ITEMS_EDITABLE_STATUSES.includes(facts.linkedPurchaseOrderStatus))
  ) {
    return deny(
      'SALES_ORDER_LINE_ITEM_PURCHASE_ORDER_LOCKED',
      facts.linkedPurchaseOrderStatus === undefined
        ? 'Locked: the linked purchase order status is unavailable.'
        : `Locked: the linked purchase order is ${getStatusLabel(facts.linkedPurchaseOrderStatus)}.`
    );
  }

  return allow();
};

/**
 * The ORDER-WIDE Products question (scan, pick, auto-pick): no row is in play, so no linked
 * purchase order narrows the answer and no row fact applies. The web grid asks it this way
 * (`UNLINKED_PRODUCT_ROW`), the api auto-pick door too, and the picking scanner arms on it.
 */
export const salesOrderOrderWideLineItemFacts = (salesOrderStatus: SalesOrderStatus): SalesOrderLineItemFacts => ({
  salesOrderStatus,
  rowKind: 'internal-parent',
  linkedPurchaseOrderStatus: null,
  priceMode: 'both',
  enteredByWeight: false,
  variableWeight: false,
  hasLot: false,
  hasAnySourceLot: false,
  isDraft: false,
  isOnEditableInvoice: false,
  isOnEditablePayable: false,
});

const fieldNotApplicable = (): Decision =>
  deny('SALES_ORDER_LINE_ITEM_FIELD_NOT_APPLICABLE', 'Not editable for this product type.');

const decideField = (facts: SalesOrderLineItemFacts, field: SalesOrderLineItemField): Decision => {
  if (facts.rowKind === 'source-child') {
    if (field === 'source' || field === 'unitsPicked') return allow();
    if (field === 'weightPicked') {
      return facts.enteredByWeight || facts.variableWeight ? allow() : fieldNotApplicable();
    }
    if (field === 'palletTagsOnLineItem') return facts.hasLot ? allow() : fieldNotApplicable();
    return fieldNotApplicable();
  }

  switch (field) {
    case 'product':
    case 'unitsOrdered':
    case 'enteredByWeight':
    case 'unitsPicked':
    case 'source':
      return allow();
    case 'external':
      return facts.rowKind !== 'internal-parent' || !facts.hasAnySourceLot ? allow() : fieldNotApplicable();
    case 'buyPrice':
      return facts.rowKind === 'external' ? allow() : fieldNotApplicable();
    case 'sellPrice':
      return facts.priceMode === 'sellPrice' || facts.priceMode === 'both' ? allow() : fieldNotApplicable();
    case 'deliveredPrice':
      return facts.priceMode === 'deliveredPrice' || facts.priceMode === 'both' ? allow() : fieldNotApplicable();
    case 'weightOrdered':
    case 'weightPicked':
      return facts.enteredByWeight || facts.variableWeight ? allow() : fieldNotApplicable();
    case 'palletTagsOnLineItem':
      return facts.rowKind !== 'external' && facts.hasLot ? allow() : fieldNotApplicable();
  }
};

const decideOperation = (facts: SalesOrderLineItemFacts, op: SalesOrderLineItemOp): Decision => {
  switch (op) {
    case 'scan':
      return facts.salesOrderStatus === 'PICKING'
        ? allow()
        : deny('SALES_ORDER_LINE_ITEM_SCAN_LOCKED', 'Scanning is available only while the order is Picking.');
    case 'pick':
    case 'autoPick':
      return facts.salesOrderStatus === 'PICKING'
        ? allow()
        : deny('SALES_ORDER_LINE_ITEM_PICK_LOCKED', 'Picking is available only while the order is Picking.');
    case 'clear':
      return facts.isDraft
        ? allow()
        : deny('SALES_ORDER_LINE_ITEM_ACTION_NOT_APPLICABLE', 'Not available for this product.');
  }
};

const fieldRule = (
  facts: SalesOrderLineItemFacts,
  action: Action<SalesOrderLineItemField, SalesOrderLineItemOp>
): Decision => (action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow());

const fieldRules = {
  product: fieldRule,
  external: fieldRule,
  buyPrice: fieldRule,
  unitsOrdered: fieldRule,
  weightOrdered: fieldRule,
  sellPrice: fieldRule,
  deliveredPrice: fieldRule,
  enteredByWeight: fieldRule,
  unitsPicked: fieldRule,
  weightPicked: fieldRule,
  source: fieldRule,
  palletTagsOnLineItem: fieldRule,
} satisfies Record<SalesOrderLineItemField, typeof fieldRule>;

export const salesOrderLineItemPolicy: Policy<SalesOrderLineItemFacts, SalesOrderLineItemField, SalesOrderLineItemOp> =
  definePolicy<SalesOrderLineItemFacts, SalesOrderLineItemField, SalesOrderLineItemOp>({
    kind: 'sales order product',
    fields: SALES_ORDER_LINE_ITEM_FIELDS,
    ops: SALES_ORDER_LINE_ITEM_OPS,
    requires: {},
    entity: (facts, action) => {
      if (action.type === 'view') return allow();
      if (action.type === 'edit' && action.field !== undefined && isPricedByEditableBill(facts, action.field)) {
        return allow();
      }

      const lock = entityLock(facts);
      if (!lock.allowed) return lock;

      return action.type === 'perform' ? decideOperation(facts, action.op) : allow();
    },
    field: fieldRules,
  });
